import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
  Brain, Plus, Sparkles, Send, Users, ClipboardList, FileText,
  Loader2, Copy, CheckCircle2, AlertTriangle, BarChart3, Download,
  UserPlus, ShieldCheck, BookOpen, Target, Zap, Library, Eye
} from "lucide-react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "personality", label: "Personalidad", icon: Brain, color: "bg-violet-100 text-violet-700" },
  { value: "intelligence", label: "Inteligencia", icon: Zap, color: "bg-blue-100 text-blue-700" },
  { value: "integrity", label: "Integridad", icon: ShieldCheck, color: "bg-red-100 text-red-700" },
  { value: "knowledge", label: "Conocimientos", icon: BookOpen, color: "bg-emerald-100 text-emerald-700" },
  { value: "competency", label: "Competencias", icon: Target, color: "bg-amber-100 text-amber-700" },
  { value: "nom035", label: "NOM-035", icon: AlertTriangle, color: "bg-orange-100 text-orange-700" },
];

const DIM_COLORS = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#06b6d4", "#84cc16"];

const Psychometric = () => {
  const [assessments, setAssessments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [testLibrary, setTestLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("library");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [jobTitle, setJobTitle] = useState("");
  const [newAssessment, setNewAssessment] = useState({ name: "", description: "", category: "personality", time_limit_minutes: 30 });
  const [assignForm, setAssignForm] = useState({ assessment_id: "", candidate_name: "", candidate_email: "" });

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
  const opts = useCallback(() => ({ headers: headers(), withCredentials: true }), [headers]);

  const fetchData = useCallback(async () => {
    try {
      const [aRes, asgnRes, empRes, libRes] = await Promise.all([
        axios.get(`${API}/assessments`, opts()),
        axios.get(`${API}/assessment-assignments`, opts()),
        axios.get(`${API}/employees`, opts()).catch(() => ({ data: [] })),
        axios.get(`${API}/test-library`, opts()),
      ]);
      setAssessments(aRes.data || []);
      setAssignments(asgnRes.data || []);
      setEmployees(empRes.data || []);
      setTestLibrary(libRes.data || []);
    } catch { toast.error("Error al cargar datos"); } finally { setLoading(false); }
  }, [opts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateFromLibrary = async (testKey) => {
    try {
      await axios.post(`${API}/test-library/${testKey}/create`, {}, opts());
      toast.success("Test agregado a tu banco");
      fetchData();
    } catch { toast.error("Error al crear test"); }
  };

  const handleCreate = async () => {
    if (!newAssessment.name) { toast.error("Nombre requerido"); return; }
    try {
      await axios.post(`${API}/assessments`, newAssessment, opts());
      toast.success("Evaluación creada");
      setShowCreateDialog(false);
      setNewAssessment({ name: "", description: "", category: "personality", time_limit_minutes: 30 });
      fetchData();
    } catch { toast.error("Error al crear"); }
  };

  const handleGenerateQuestions = async (assessmentId, topic) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/assessments/${assessmentId}/generate-questions`, { count: 12, difficulty: "medium", topic }, opts());
      toast.success(`${res.data.count} preguntas generadas con IA`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setGenerating(false); }
  };

  const handleAssign = async () => {
    if (!assignForm.assessment_id || !assignForm.candidate_name) { toast.error("Datos requeridos"); return; }
    try {
      const res = await axios.post(`${API}/assessments/assign`, assignForm, opts());
      toast.success(`Asignada. Código: ${res.data.access_code}`);
      setShowAssignDialog(false);
      setAssignForm({ assessment_id: "", candidate_name: "", candidate_email: "" });
      fetchData();
    } catch { toast.error("Error al asignar"); }
  };

  const handleAnalyze = async (assignmentId) => {
    setAnalyzing(true); setAnalysisResult(null); setShowAnalysisDialog(true);
    try {
      const res = await axios.post(`${API}/psychometric/analyze/${assignmentId}`, {}, opts());
      setAnalysisResult(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); setShowAnalysisDialog(false); }
    finally { setAnalyzing(false); }
  };

  const handleCompare = async () => {
    if (compareIds.length < 2) { toast.error("Selecciona al menos 2"); return; }
    setComparing(true); setCompareResult(null);
    try {
      const res = await axios.post(`${API}/psychometric/compare`, { assignment_ids: compareIds, job_title: jobTitle }, opts());
      setCompareResult(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setComparing(false); }
  };

  const handleDownloadPDF = async (assignmentId) => {
    try {
      const res = await axios.post(`${API}/psychometric/generate-report/${assignmentId}`, {}, { ...opts(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `psicometrico_${assignmentId}.pdf`; a.click();
      toast.success("PDF descargado");
    } catch { toast.error("Error al generar PDF"); }
  };

  const handleCreateNOM035 = async () => {
    try { await axios.post(`${API}/nom035/create-default`, {}, opts()); toast.success("NOM-035 creado"); fetchData(); }
    catch { toast.error("Error"); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success("Código copiado"); };
  const getCatInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];
  const completedAssignments = assignments.filter(a => a.status === "completed");

  const DimensionChart = ({ dimensions, labels }) => {
    if (!dimensions || Object.keys(dimensions).length === 0) return null;
    const data = Object.entries(dimensions).map(([k, v]) => ({
      subject: labels?.[k] || k, pct: Math.round((v.total / Math.max(v.max, 1)) * 100), fullMark: 100,
    }));
    if (data.length >= 3) {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <RadarChart data={data}><PolarGrid strokeDasharray="3 3" /><PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }} /><Radar dataKey="pct" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} strokeWidth={2} />
          </RadarChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} layout="vertical"><CartesianGrid strokeDasharray="3 3" /><XAxis type="number" domain={[0, 100]} /><YAxis type="category" dataKey="subject" width={120} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => `${v}%`} /><Bar dataKey="pct" radius={[0, 6, 6, 0]}>{data.map((_, i) => <Cell key={i} fill={DIM_COLORS[i % DIM_COLORS.length]} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  if (loading) return <Layout title="Psicometría IA"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout title="Psicometría IA" subtitle="Tests psicométricos científicos + inteligencia artificial"
      actions={
        <div className="flex gap-2">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild><Button variant="outline" data-testid="create-custom-btn"><Plus className="w-4 h-4 mr-2" />Personalizado</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Crear Test Personalizado</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Nombre</Label><Input value={newAssessment.name} onChange={e => setNewAssessment({ ...newAssessment, name: e.target.value })} placeholder="Ej: Test Técnico - DevOps" data-testid="assessment-name" /></div>
                <div><Label>Categoría</Label>
                  <Select value={newAssessment.category} onValueChange={v => setNewAssessment({ ...newAssessment, category: v })}>
                    <SelectTrigger data-testid="assessment-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Descripción</Label><Textarea rows={2} value={newAssessment.description} onChange={e => setNewAssessment({ ...newAssessment, description: e.target.value })} /></div>
                <div><Label>Tiempo límite (min)</Label><Input type="number" value={newAssessment.time_limit_minutes} onChange={e => setNewAssessment({ ...newAssessment, time_limit_minutes: parseInt(e.target.value) || 30 })} /></div>
                <Button onClick={handleCreate} className="w-full" data-testid="save-assessment-btn">Crear</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
            <DialogTrigger asChild><Button data-testid="assign-btn"><UserPlus className="w-4 h-4 mr-2" />Asignar</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Asignar Evaluación</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Evaluación</Label>
                  <Select value={assignForm.assessment_id} onValueChange={v => setAssignForm({ ...assignForm, assessment_id: v })}>
                    <SelectTrigger data-testid="assign-assessment"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>{assessments.filter(a => (a.questions || []).length > 0).map(a => <SelectItem key={a.assessment_id} value={a.assessment_id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Nombre del candidato</Label><Input value={assignForm.candidate_name} onChange={e => setAssignForm({ ...assignForm, candidate_name: e.target.value })} data-testid="candidate-name" /></div>
                <div><Label>Email</Label><Input type="email" value={assignForm.candidate_email} onChange={e => setAssignForm({ ...assignForm, candidate_email: e.target.value })} /></div>
                <Button onClick={handleAssign} className="w-full" data-testid="confirm-assign-btn"><Send className="w-4 h-4 mr-2" />Asignar y Generar Código</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="psychometric-stats">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center"><Library className="w-5 h-5 text-violet-600" /></div><div><p className="text-2xl font-bold">{testLibrary.length}</p><p className="text-sm text-slate-500">En Biblioteca</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Brain className="w-5 h-5 text-blue-600" /></div><div><p className="text-2xl font-bold">{assessments.length}</p><p className="text-sm text-slate-500">Mis Tests</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div><div><p className="text-2xl font-bold">{completedAssignments.length}</p><p className="text-sm text-slate-500">Completadas</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><Sparkles className="w-5 h-5 text-amber-600" /></div><div><p className="text-2xl font-bold">{assignments.filter(a => a.ai_analysis).length}</p><p className="text-sm text-slate-500">Analizadas IA</p></div></CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6" data-testid="psychometric-tabs">
          <TabsTrigger value="library"><Library className="w-4 h-4 mr-1" />Biblioteca</TabsTrigger>
          <TabsTrigger value="tests"><Brain className="w-4 h-4 mr-1" />Mis Tests</TabsTrigger>
          <TabsTrigger value="assignments"><ClipboardList className="w-4 h-4 mr-1" />Asignaciones</TabsTrigger>
          <TabsTrigger value="compare"><BarChart3 className="w-4 h-4 mr-1" />Comparar</TabsTrigger>
        </TabsList>

        {/* LIBRARY */}
        <TabsContent value="library">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="test-library">
            {testLibrary.map(t => {
              const cat = getCatInfo(t.category);
              const Icon = cat.icon;
              const alreadyAdded = assessments.some(a => a.library_key === t.key);
              return (
                <Card key={t.key} className="group hover:shadow-lg transition-all border-slate-200 hover:border-violet-300" data-testid={`lib-${t.key}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-xl ${cat.color} flex items-center justify-center shrink-0`}><Icon className="w-6 h-6" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900 text-sm leading-tight">{t.name}</p>
                        <div className="flex gap-1 mt-1"><Badge className={`${cat.color} text-[10px]`}>{cat.label}</Badge><Badge variant="outline" className="text-[10px]">{t.question_count} items</Badge><Badge variant="outline" className="text-[10px]">{t.time_limit_minutes} min</Badge></div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mb-3 line-clamp-2">{t.description}</p>
                    <div className="flex flex-wrap gap-1 mb-3">{t.dimensions?.map(d => <span key={d} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{d}</span>)}</div>
                    <Button size="sm" className={alreadyAdded ? "w-full bg-slate-200 text-slate-500" : "w-full bg-violet-600 hover:bg-violet-700"} disabled={alreadyAdded} onClick={() => handleCreateFromLibrary(t.key)} data-testid={`add-${t.key}`}>
                      {alreadyAdded ? <><CheckCircle2 className="w-3 h-3 mr-1" />Ya agregado</> : <><Plus className="w-3 h-3 mr-1" />Agregar a mis tests</>}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            {/* NOM-035 Special */}
            <Card className="group hover:shadow-lg transition-all border-orange-200 hover:border-orange-400" data-testid="lib-nom035">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-orange-600" /></div>
                  <div><p className="font-bold text-slate-900 text-sm">NOM-035-STPS-2018</p><div className="flex gap-1 mt-1"><Badge className="bg-orange-100 text-orange-700 text-[10px]">Cumplimiento</Badge><Badge variant="outline" className="text-[10px]">12 items</Badge></div></div>
                </div>
                <p className="text-xs text-slate-500 mb-3">Cuestionario oficial para identificar factores de riesgo psicosocial en el trabajo. Obligatorio para empresas en México.</p>
                <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700" onClick={handleCreateNOM035} data-testid="create-nom035-btn"><AlertTriangle className="w-3 h-3 mr-1" />Crear NOM-035</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* MY TESTS */}
        <TabsContent value="tests">
          {assessments.length === 0 ? (
            <Card><CardContent className="p-12 text-center" data-testid="tests-empty"><Brain className="w-16 h-16 mx-auto mb-4 text-slate-300" /><h3 className="text-lg font-semibold mb-2">Sin evaluaciones</h3><p className="text-slate-500">Agrega tests desde la Biblioteca o crea uno personalizado</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assessments.map(a => {
                const cat = getCatInfo(a.category);
                const Icon = cat.icon;
                const qCount = (a.questions || []).length;
                return (
                  <Card key={a.assessment_id} className="hover:shadow-md transition-all" data-testid={`test-${a.assessment_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center shrink-0`}><Icon className="w-5 h-5" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{a.name}</p>
                          <div className="flex gap-1 mt-1"><Badge className={`${cat.color} text-[10px]`}>{cat.label}</Badge>{a.is_library && <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-600">Científico</Badge>}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-3"><span>{qCount} preguntas</span><span>{a.time_limit_minutes} min</span></div>
                      <div className="flex gap-2">
                        {qCount === 0 ? (
                          <Button size="sm" onClick={() => handleGenerateQuestions(a.assessment_id, a.name)} disabled={generating} data-testid={`gen-${a.assessment_id}`}>
                            {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}Generar IA
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => { setAssignForm({ ...assignForm, assessment_id: a.assessment_id }); setShowAssignDialog(true); }} data-testid={`assign-${a.assessment_id}`}><Send className="w-3 h-3 mr-1" />Asignar</Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ASSIGNMENTS */}
        <TabsContent value="assignments">
          {assignments.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="assignments-empty"><ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">Sin asignaciones</p></CardContent></Card>
          ) : (
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Candidato</TableHead><TableHead>Evaluación</TableHead><TableHead>Código</TableHead><TableHead>Estado</TableHead><TableHead>Score</TableHead><TableHead>Acciones</TableHead></TableRow></TableHeader>
                <TableBody>
                  {assignments.map(a => {
                    const asmt = assessments.find(x => x.assessment_id === a.assessment_id);
                    return (
                      <TableRow key={a.assignment_id} data-testid={`asgn-${a.assignment_id}`}>
                        <TableCell><p className="font-medium text-sm">{a.candidate_name}</p><p className="text-xs text-slate-400">{a.candidate_email}</p></TableCell>
                        <TableCell className="text-sm">{asmt?.name || a.assessment_id}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1"><code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">{a.access_code}</code>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyCode(a.access_code)}><Copy className="w-3 h-3" /></Button></div>
                        </TableCell>
                        <TableCell><Badge className={a.status === "completed" ? "bg-emerald-100 text-emerald-700" : a.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>{a.status === "completed" ? "Completada" : a.status === "in_progress" ? "En Progreso" : "Pendiente"}</Badge></TableCell>
                        <TableCell>{a.score != null ? <span className="font-bold text-sm">{a.score}%</span> : "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {a.status === "completed" && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => handleAnalyze(a.assignment_id)} data-testid={`analyze-${a.assignment_id}`}><Sparkles className="w-3 h-3 mr-1" />IA</Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDownloadPDF(a.assignment_id)}><Download className="w-3 h-3" /></Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* COMPARE */}
        <TabsContent value="compare">
          <Card data-testid="compare-section">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-600" />Comparar Candidatos con IA</CardTitle></CardHeader>
            <CardContent>
              {completedAssignments.length < 2 ? (
                <div className="text-center py-8 text-slate-500"><Users className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">Se necesitan al menos 2 evaluaciones completadas</p></div>
              ) : (
                <div className="space-y-4">
                  <div><Label>Puesto a evaluar</Label><Input value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="Ej: Gerente de Ventas" data-testid="compare-job-title" /></div>
                  <div><Label>Seleccionar candidatos</Label>
                    <div className="space-y-2 mt-2">{completedAssignments.map(a => {
                      const asmt = assessments.find(x => x.assessment_id === a.assessment_id);
                      return (
                        <label key={a.assignment_id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 cursor-pointer hover:bg-slate-100">
                          <input type="checkbox" checked={compareIds.includes(a.assignment_id)} onChange={e => e.target.checked ? setCompareIds([...compareIds, a.assignment_id]) : setCompareIds(compareIds.filter(id => id !== a.assignment_id))} className="accent-violet-600" />
                          <div className="flex-1"><p className="text-sm font-medium">{a.candidate_name}</p><p className="text-xs text-slate-500">{asmt?.name} | Score: {a.score}%</p></div>
                        </label>
                      );
                    })}</div>
                  </div>
                  <Button onClick={handleCompare} disabled={comparing || compareIds.length < 2} className="bg-violet-600 hover:bg-violet-700" data-testid="compare-btn">
                    {comparing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Comparar con IA
                  </Button>
                  {compareResult && (
                    <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-4"><h4 className="font-semibold text-violet-800 mb-2">Resultado de Comparación IA</h4><div className="text-sm text-slate-700 whitespace-pre-wrap">{compareResult.comparison}</div></div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* AI Analysis Dialog */}
      <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-500" />Análisis Psicométrico IA</DialogTitle></DialogHeader>
          {analyzing ? (
            <div className="flex flex-col items-center py-12"><Loader2 className="w-10 h-10 text-violet-500 animate-spin mb-4" /><p className="text-slate-500">Analizando con GPT-5.2...</p><p className="text-xs text-slate-400 mt-1">Generando informe profesional</p></div>
          ) : analysisResult ? (
            <div className="space-y-4">
              <div className="flex items-center gap-6 p-4 rounded-xl bg-slate-50">
                <div className="text-center"><p className="text-4xl font-bold text-violet-600">{analysisResult.score}%</p><p className="text-xs text-slate-500">Score Global</p></div>
                <div className="flex-1"><DimensionChart dimensions={analysisResult.dimensions} labels={analysisResult.dimensions_labels} /></div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{analysisResult.analysis}</div></div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Psychometric;
